import React, { useEffect, useState } from "react";
import Header from "./HeaderComponent";
import FooterComponent from "./FooterComponent";
import { publishedDate, termsSections } from "../data/termsAndConditionsContent";
import "../assets/css/terms_and_conditions.css";

// Matches URLs (with or without scheme) and email addresses inside clause text.
const LINK_PATTERN =
  /(https?:\/\/[^\s]+|www\.[^\s]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

/**
  Turns any URL or email address in the given text into a hyperlink.
*/
function linkify(text) {
  if (!text) {
    return null;
  }
  // Splitting on a capturing pattern yields text and matches alternately.
  return text.split(LINK_PATTERN).map((part, index) => {
    if (index % 2 === 0) {
      return part;
    }
    // Sentence punctuation stuck to the end of a link is not part of it.
    const trailing = part.match(/[.,;:)\]]+$/);
    const suffix = trailing ? trailing[0] : "";
    const target = suffix ? part.slice(0, -suffix.length) : part;
    const hasScheme = /^https?:\/\//.test(target);
    const isEmail = !hasScheme && target.includes("@");
    const href = isEmail
      ? `mailto:${target}`
      : hasScheme
      ? target
      : `https://${target}`;
    return (
      <React.Fragment key={index}>
        <a
          className="terms_link"
          href={href}
          {...(isEmail ? {} : { target: "_blank", rel: "noreferrer" })}
        >
          {target}
        </a>
        {suffix}
      </React.Fragment>
    );
  });
}

// A clause usually opens with a short lead-in phrase — "Purpose Limitation:",
// "Applicability.", or a defined term followed by an em dash. That phrase is
// rendered in bold. Content can override the detection with an explicit `lead`.
const LEAD_PATTERN = /^([A-Z][^,;:.—]{0,60}?)(:|\.|\s—)(\s|$)/;

/**
  Splits a lead-in phrase off the front of the given text, returning
  [lead, remainder]. The lead is null when the text does not open with one.
*/
function splitLead(text, explicitLead) {
  if (!text) {
    return [explicitLead || null, ""];
  }
  if (explicitLead) {
    return [
      explicitLead,
      text.startsWith(explicitLead) ? text.slice(explicitLead.length) : text,
    ];
  }
  // `lead: false` opts a clause out where the opening sentence only reads like
  // a lead-in phrase.
  const match = explicitLead === false ? null : text.match(LEAD_PATTERN);
  if (!match) {
    return [null, text];
  }
  const [, phrase, delimiter] = match;
  if (phrase.trim().split(/\s+/).length > 8) {
    return [null, text];
  }
  // An em dash stays with the body; a colon or full stop belongs to the lead.
  const lead = delimiter === ":" || delimiter === "." ? phrase + delimiter : phrase;
  const remainder = text.slice(lead.length);
  // A whole clause that matched is a sentence, not a lead-in phrase.
  return remainder.trim() ? [lead, remainder] : [null, text];
}

/**
  Renders clause text with its lead-in phrase in bold and its links live.
*/
function TermsText({ text, lead }) {
  const [leadPhrase, remainder] = splitLead(text, lead);
  return (
    <>
      {leadPhrase && <strong className="terms_lead">{leadPhrase}</strong>}
      {linkify(remainder)}
    </>
  );
}

/**
  Renders a lettered list of clause items, nesting further lists as needed.
*/
function TermsList({ list }) {
  return (
    <ol
      type="a"
      className={list.className || "custom-ordered-list text-left ps-0"}
    >
      {list.items.map((item, index) => (
        <div className={list.itemClassName || "terms d-flex"} key={index}>
          <span className="me-3">{item.label}&nbsp;</span>
          <span>
            <TermsText text={item.text} lead={item.lead} />
            {item.list && <TermsList list={item.list} />}
          </span>
        </div>
      ))}
    </ol>
  );
}

/**
  Renders a single numbered clause. Its list sits inside the clause text unless
  the content marks it as `inline: false`.
*/
function TermsClause({ clause }) {
  const isInlineList = clause.list && clause.list.inline !== false;
  return (
    <>
      <div className="terms_text mb-2 d-flex">
        <span className="me-2">{clause.number}.&nbsp;</span>
        <span>
          {clause.heading && (
            <span className="text-underline text-decoration-underline">
              {clause.heading}
            </span>
          )}
          <TermsText text={clause.text} lead={clause.lead} />
          {isInlineList && <TermsList list={clause.list} />}
        </span>
      </div>
      {clause.list && !isInlineList && <TermsList list={clause.list} />}
    </>
  );
}

/**
  Renders a numbered section heading followed by its clauses.
*/
function TermsSection({ section }) {
  return (
    <>
      <div className="terms_section_sub_heads">
        {section.number}. &nbsp;
        <span className="text-underline text-decoration-underline">
          {section.title}
        </span>
      </div>
      {section.clauses.map((clause) => (
        <TermsClause clause={clause} key={clause.number} />
      ))}
    </>
  );
}

/**
  Shows Terms and Conditons page
*/
function TermsAndConditionsComponent({ flag }) {
  const [hideHeader, setHideHeader] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);
  const [isFAQ, handleFAQVisibility] = useState(false);

  useEffect(() => {
    const scroll_to = document.getElementById("terms_section");
    if (scroll_to) {
      scroll_to.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    // Parse URL parameters on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const hideFooter = urlParams.get('hideFooter');
    const hideHeader = urlParams.get('hideHeader');
    const hideChatButton = urlParams.get('hideChatButton');
    const isFAQ = urlParams.get('isFAQ');
    if (hideFooter === 'true') {
      setHideFooter(true);
    }
    if(hideHeader === 'true') {
      setHideHeader(true);
    }
    if(hideChatButton === 'true') {
        window.fcWidgetMessengerConfig = {
          config: {
            headerProperty:{
              hideChatButton: true,
            }
          }
        }
    }
    if(isFAQ === 'true') {
      window.fcWidgetMessengerConfig = {
        open: true,
      }
      handleFAQVisibility(true);
    }
  }, []);

  return (
   !isFAQ && <div id="terms_section">
     { !hideHeader && 
      <div id="terms_header">
        <Header />
      </div>}
      <div className="container" id="terms_container">
        <div className="col-md-12">
          <div className="col-md-6 m-auto text-center px-2 terms_div">
            <div className="terms_published">Published {publishedDate}</div>
            <div id="terms_head">TERMS & CONDITIONS</div>
          </div>
        </div>
      </div>
      <div className="container" id="terms_container_intro">
        <div className="col-md-6 m-auto text-left terms_div">
          {termsSections.map((section) => (
            <TermsSection section={section} key={section.number} />
          ))}
        </div>
      </div>
      {!hideFooter && <div className="Terms_footer">
        <FooterComponent />
      </div>}
    </div>
  );
}

export default TermsAndConditionsComponent;
